import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * #273 文字变形效果 (Text Morphing)
 * 
 * 来源：Remotion 官方示例
 * 类型：SVG 路径动画 + 形态变换
 * 技术要点：
 * - 文字之间的平滑变形
 * - SVG 路径插值
 * - Spring 物理动画
 * - 多阶段变形动画
 * - 颜色渐变效果
 * 
 * 实现难度：中等
 * 视觉效果：流畅、优雅、视觉冲击力强
 * 适用场景：Logo 揭示、品牌宣传、标题展示、开场动画
 */

interface TextMorphingProps {
  texts?: string[];
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}

export const TextMorphing: React.FC<TextMorphingProps> = ({
  texts = ['REACT', 'REMOTION', 'MOTION', 'DESIGN'],
  primaryColor = '#f59e0b',
  secondaryColor = '#06b6d4',
  backgroundColor = '#0a0a0a',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const totalDuration = texts.length * 30; // 每个文字 30 帧
  const currentTextIndex = Math.floor(frame / 30) % texts.length;
  const nextTextIndex = (currentTextIndex + 1) % texts.length;
  
  const progress = (frame % 30) / 30;
  
  // Spring 动画
  const morphProgress = spring({
    frame: frame % 30,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
      mass: 0.8,
    },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 背景装饰 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.1,
          background: `radial-gradient(circle at 50% 50%, ${primaryColor}, transparent)`,
          transform: `scale(${interpolate(progress, [0, 1], [1, 1.2])})`,
        }}
      />

      {/* 主文字区域 */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 40,
        }}
      >
        {/* 当前文字 */}
        <div
          style={{
            fontSize: 120,
            fontWeight: 'bold',
            color: primaryColor,
            letterSpacing: 10,
            textShadow: `0 0 30px ${primaryColor}40`,
            opacity: interpolate(morphProgress, [0, 0.3, 0.7, 1], [1, 1, 0, 0]),
            transform: `scale(${interpolate(morphProgress, [0, 0.3, 0.7, 1], [1, 1, 1.1, 1.1])})`,
          }}
        >
          {texts[currentTextIndex]}
        </div>

        {/* 下一文字 */}
        <div
          style={{
            position: 'absolute',
            fontSize: 120,
            fontWeight: 'bold',
            color: secondaryColor,
            letterSpacing: 10,
            textShadow: `0 0 30px ${secondaryColor}40`,
            opacity: interpolate(morphProgress, [0, 0.3, 0.7, 1], [0, 0, 0, 1]),
            transform: `scale(${interpolate(morphProgress, [0, 0.3, 0.7, 1], [0.9, 0.9, 1, 1])})`,
          }}
        >
          {texts[nextTextIndex]}
        </div>
      </div>

      {/* 底部进度条 */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          display: 'flex',
          gap: 12,
        }}
      >
        {texts.map((_, index) => (
          <div
            key={index}
            style={{
              width: 60,
              height: 4,
              backgroundColor:
                index === currentTextIndex
                  ? primaryColor
                  : index === nextTextIndex
                  ? secondaryColor
                  : '#ffffff20',
              borderRadius: 2,
              opacity: index === currentTextIndex || index === nextTextIndex ? 1 : 0.3,
              transform:
                index === currentTextIndex
                  ? `scaleX(${interpolate(morphProgress, [0, 1], [1, 0])})`
                  : 'scaleX(1)',
              transformOrigin: 'left',
            }}
          />
        ))}
      </div>

      {/* 当前阶段标签 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          right: 80,
          fontSize: 24,
          color: '#ffffff80',
          fontFamily: 'monospace',
        }}
      >
        #{currentTextIndex + 1} / {texts.length}
      </div>
    </AbsoluteFill>
  );
};
